import {inject} from "../services/di";
import {ShadowElement} from "./ShadowElement";
import * as array from "../utils/array";
import * as uuid from "../utils/uuid";
import {ErrorService} from "../services/ErrorService";

/**
 * Decorator to convieniently define component attributes.
 * Example:
 * ```
 * export class HTMLFormElement {
 *   @attr acceptCharset: string;
 * }
 *
 * // form.acceptCharset = '?'; // sets attribute 'accept-charset'
 * // form.acceptCharset === form.getAttribute('accept-charset')
 * ```
 *
 */
// tslint:disable-next-line:no-unused-variable
export function attr(target: any, key: string): void {
	// camelCase to attribute-case
	const attributeName = key.replace(/[A-Z]/,
			(cap) => "-" + cap.toLowerCase());
	const type = Reflect.getMetadata("design:type", target, key);

	Object.defineProperty(target, key, {
		enumerable: true,
		get: function() {
			if (type === String) {
				return this.getAttribute(attributeName);
			} else if (type === Number) {
				return this.hasAttribute(attributeName) ?
						parseFloat(this.getAttribute(attributeName)) : null;
			} else if (type === Boolean) {
				return this.hasAttribute(attributeName);
			}
		},
		set: function(v: any) {
			if (v === null || v === undefined) {
				this.removeAttribute(attributeName);
			} else if (type === Boolean) {
				if (v) {
					this.setAttribute(attributeName, "");
				} else {
					this.removeAttribute(attributeName);
				}
			} else {
				this.setAttribute(attributeName, String(v));
			}
		}
	});
}

/**
 * Decorator to convieniently define component property getter and setters
 * This is mainly a convience for passing callback functions between components
 *
 * Example:
 * ```
 * export class CouponsPage {
 *   @handler selectionCallback: () => void;
 * }
 *
 * // this.selectionCallback()
 * ```
 *
 */
export function handler(target: any, key: string): void {
	Object.defineProperty(target, key, {
		enumerable: true,
		get: function() {
			return this[`__${key}`];
		},
		set: function(v: any) {
			if (v === null || v === undefined) {
				this[`__${key}`] = undefined;
				return;
			}
			this[`__${key}`] = v;
		}
	});
}

class ShadowDOMContentElementWidget implements virtualDom.Widget {
	type = "Widget";

	constructor(protected nodes: Node[]) {}

	init(): Element {
		const frag = document.createDocumentFragment();
		this.nodes.forEach((node) => frag.appendChild(node));
		return <Element>(frag as any);
	}
	update(previous: virtualDom.Widget, domNode: Element) {}
	destroy(node: Element) {}
}

function reduceChildKeys(children: virtualDom.VNode[]) {
	let keys: string[] = [];
	children.forEach((child) => {
		if (child.key) {
			keys.push(child.key);
		}
		if (child.children) {
			keys = keys.concat(reduceChildKeys(<any>child.children));
		}
	});
	return keys;
}

export class Component<Props, Model> extends ShadowElement {
	static tagName: string;

	@inject error: ErrorService;

	protected refs: {} = {};
	protected attached = false;

	state: {} = {};
	model: Model = null;

	props: Props = (<Props> {});
	protected _transcludedVTree: virtualDom.VNode[];

	/* Virtual node representation of this component. All changes to this component
	 * should be diff'd against this. Eg:
	 *
	 *  let vRoot = <some new DOM>;
	 *  let diff = virtualDom.diff(this.vNode, vRoot);
	 *	virtualDom.patch(this, diff); */
	vNode: virtualDom.VNode;

	private isRendering_: boolean = false;

	// We need to seperate the legacy style `createElement` from the new style.
	// tslint:disable-next-line:no-unused-variable
	private COMPONENT_STYLE_CREATE_ELEMENT = true;

	private _childComponents: Component<any, any>[] = [];

	private _currentInvalidation: Promise<void> = null;

	/* Assign a 'render sequence' to each invalidation. We use this to uniquely identify an invalidation.
	 * This is handy in specific cases such as when renderLoading() is called. When a component implements
	 * renderLoading(), the invalidation algorithm only executes renderLoading() before the render promise
	 * is resolved. This can be a problem in cases where invalidate() is called on a component with an
	 * outstanding Promise to render full content. Consider -
	 *
	 * Component.invalidate() is called:
	 *  1) prepare() returns a Promise
	 * 	2) renderLoading() is implemented, returns valid markup
	 *  3) Async. set off call to render() when promise returned from (1) invalidates
	 *  4) Component updates DOM
	 *  5) invalidate promise returned
	 *  6) Component.invalidate() is called again
	 *
	 * If step 6 is executed BEFORE the Promise created in step 3 is resolved, then there may
	 * be weird output depending on the order in which they resolve and update the DOM.
	 *
	 * To work around this, we assign a 'render sequence' and a Promise in an invalidation cycle
	 * can only update the DOM if the sequence number is the same as the most recent sequence number
	 *
	 * This would be a lot easier if we were able to CANCEL a Promise, but we have to live with
	 * what we have!
	 * */
	private _renderSeq: string;

	createdCallback() {
		try {
			// Create VNode for this component
			this.vNode = new virtualDom.VNode(this.tagName, this.props, []);

			this.onCreate();
		} catch (e) {
			this.error.reportError(e);
		}
	}

	attachedCallback() {
		try {
			this.attached = true;
			this.onAttach();
		} catch (e) {
			this.error.reportError(e);
		}
	}

	detachedCallback() {
		try {
			this._currentInvalidation = null;
			this.attached = false;
			this.onDetach();
		} catch (e) {
			this.error.reportError(e);
		}
	}

	attributeChangedCallback(name: string, oldVal: string, newVal: string) {
		try {
			(<any> this.props)[name] = newVal;

			if (name === "ref" && !newVal) {
				/* Extra guard case to ensure 'ref' attributes do not trigger an extra render
				* when they are removed from the DOM */
				return;
			}
			this.onAttributeChange(name, oldVal, newVal);
		} catch (e) {
			this.error.reportError(e);
		}
	}

	/**
	 * Invalidate the component, causing an eventual re-render.
	 * Components implementing renderLoading() can optionally provide a boolean 'waitForRender' which
	 * will only resolve the invalidation promise once render() is complete instead of renderingLoading()
	 *
	 * @param waitForRender: If true and renderLoading() is defined, waits for render() to finish
	 * 												before resolving invalidation promise instead of renderLoading()
	 * @final
	 */
	invalidate(waitForRender = false): Promise<void> {
		if (!this._currentInvalidation) {
			this._renderSeq = uuid.generateUUID();
			this._currentInvalidation = this._invalidateInternal(waitForRender)
				.catch((e: Error) => {
					this.error.reportError(e);
				}
			);
		}

		return this._currentInvalidation;
	}

	/**
	 * Restore the state of the component.
	 */
	restore(state: any) {
		if (state === null || state === undefined) return;

		try {
			this.state = state;
			this.onRestore(this.state);
		} catch (e) {
			this.error.reportError(e);
		}
	}

	// Override in subclass to render.
	protected prepare(): Model|Promise<Model> { return undefined; }
	protected render(model: Model): JSX.Element { return undefined; }

	protected renderLoading(): JSX.Element { return undefined; }

	// TODO: renderError is not implemented. Evaluate need
	protected renderError(error: any): JSX.Element { return undefined; }

	get childComponents() {
		return this._childComponents.slice();
	}

	//
	onCreate() {}
	onAttach() {}
	onDetach() {}
	onAttributeChange(name: string, oldVal: string, newVal: string) {
		this.invalidate();
	}

	onRender() {}

	// FIXME: Do we want to type this?
	onRestore(state: any) {}

	setTranscludedVTreeInternal(transcluded: virtualDom.VNode[]) {
		this._transcludedVTree = transcluded;
	}

	setVNodeInternal(vNode: virtualDom.VNode) {
		/* Overrides the virtual node created for this Component with an vNode.
		 * This is necessary because Component objects are backed by ComponentWidget vdom
		 * Widgets which are initialized on diff(). Once they are initialized, we need to
		 * set the set the new vNode from the ComponentWidget into this Component */
		this.vNode = vNode;
		this.props = (<Props> vNode.properties);
	}

	private _executeRender(prepare: Model | Promise<Model>, parentRenderSeq?: string) {
		return Promise.resolve()
			.then(() => {
				if (this._renderSeq && parentRenderSeq && this._renderSeq !== parentRenderSeq) return;
				return prepare;
			})
			.then((model) => {
				if (this._renderSeq && parentRenderSeq && this._renderSeq !== parentRenderSeq) return;
				if (model === null) return;

				this.model = model;
				return this.renderInternal_(this.model);
			}).catch((err: any) => {
				if (this._renderSeq && parentRenderSeq && this._renderSeq !== parentRenderSeq) return;
				this._currentInvalidation = null;
				this.model = null;

				// FIXME [FW-947] implement error states.
				throw err;
			});
	}

	private _doRenderLoading(): HTMLElement {
		flipp.setVirtualDOM(true);
		const loadingDom = this.renderLoading();
		flipp.setVirtualDOM(false);
		return loadingDom;
	}

	private _invalidateInternal(waitForRender: boolean): Promise<void> {
		return Promise.resolve().then(() => {
			this.isRendering_ = true;
		}).then(() => {
			const prepare = this.prepare();
			if (prepare instanceof Promise) {
				const loadingDom = this._doRenderLoading();
				if (loadingDom) {
					return this.renderLoadingInternal_(loadingDom).then(() => {
						if (waitForRender) {
							return this._executeRender(prepare, this._renderSeq);
						} else {
							this._executeRender(prepare, this._renderSeq);
						}
					});
				} else {
					return this._executeRender(prepare);
				}
			} else {
				return this._executeRender(prepare);
			}
		}).then(() => {
			this._currentInvalidation = null;
			this._renderSeq = null;
		}).catch((err: any) => {
			this._currentInvalidation = null;
			this._renderSeq = null;
			this.model = null;

			// FIXME [FW-947] implement error states.
			throw err;
		});
	}

	private renderLoadingInternal_(dom: JSX.Element): Promise<void> {

		const castedDom = <any>dom;
		let vRoot: virtualDom.VNode = null;

		if (dom && dom.tagName === "template") {
			if (this._transcludedVTree) {
				vRoot = this._doVirtualTransclusion(castedDom);
			} else {
				vRoot = this._doNonVirtualTransclusion(castedDom);
			}
		} else {
			vRoot = new virtualDom.VNode(this.tagName, this.props, castedDom ? [castedDom] : []);
		}

		let diff = virtualDom.diff(this.vNode, vRoot);
		virtualDom.patch(this as Element, diff);
		this.vNode = vRoot;

		// return a promise to the child invalidation
		this._childComponents = this._getChildren(this as Element);
		this._recordRefs();
		return Promise.all(this._childComponents.map((child) => child.invalidate()))
			.then(() => {
				this._recordRefs();
			});
	}

	private renderInternal_(model: Model): Promise<void> {

		flipp.setVirtualDOM(true);
		const dom: any = this.render(model);
		let vRoot: virtualDom.VNode = null;

		if (dom && dom.tagName === "template") {
			if (this._transcludedVTree) {
				vRoot = this._doVirtualTransclusion(dom);
			} else {
				vRoot = this._doNonVirtualTransclusion(dom);
			}
		} else {
			vRoot = new virtualDom.VNode(this.tagName, this.props, dom ? [dom] : []);
		}

		let diff = virtualDom.diff(this.vNode, vRoot);
		virtualDom.patch(this as Element, diff);
		this.vNode = vRoot;
		flipp.setVirtualDOM(false);

		// return a promise to the child invalidation
		this._childComponents = this._getChildren(this as Element);
		this._recordRefs();
		return Promise.all(this._childComponents.map((child) => child.invalidate()))
				.then(() => {
					this._recordRefs();
					this.isRendering_ = false;
					this.onRender();
				});
	}

	private _getChildren(node: Element): Component<any, any>[] {
		let components: Component<any, any>[] = [];
		if (node.children) {
			for (let idx = 0; idx < node.children.length; idx++) {
				const child = node.children.item(idx);
				if ("COMPONENT_STYLE_CREATE_ELEMENT" in child) {
					components.push(child as Component<any, any>);
				} else {
					components = components.concat(this._getChildren(child));
				}
			}
		}
		return Array.prototype.slice.call(components);
	}

	private _recordRefs() {
		const refEls: Element[] =
				Array.prototype.slice.call(this.querySelectorAll("*[ref]"));
		const childKeys = this._transcludedVTree ? reduceChildKeys(this._transcludedVTree) : [];
		refEls.forEach((el) => {
			const refName = el.getAttribute("ref");
			const matchingKey = array.find(childKeys, (key) => key === refName);
			if (!matchingKey) {
				/* If ref is contributed by trancluded content, don't assign as ref in
				* this component and instead allow it to be picked by
				*
				* Consider the following example components:
				* CouponsPage
				*  - Page (ref=page, on CouponsPage)
				* 	 - button (ref=submit, on CouponsPage)
				*
				* Steps:
				* 1) CouponsPage component will render first
				* 	- Under the covers, createVirtualElement will be called (in Templates.ts) since
				* 		setVirtualDOM is set to true if we're here
				* 2) Step above will trigger Page to render since it's a child of CouponsPage. A
				* 	ComponentWidget (vdom Widget) class will be created
				* 3) After CouponsPage#render returns, we diff()/patch() the DOM and this triggers
				* 	the initialization of the above ComponentWidget. This operations sets the
				*  transcluded childen on the Page class (Page#setTranscludedVTreeInternal)
				* 4) After CouponsPage renders, it will render its children (Page)
				* 5) After calling Page#render, we check for transcluded children. These were set in
				* 	step (3)
				* 6) Transclution is performed - The children are transcluded into the Page component
				* 7) Problem: after transclution, we check for 'refs' on the children components. If
				* 	found, they are assigned as refs to the current component. In the example, since
				*  we're rendering Page (in step 5), all refs contributed by transcluded children will be
				*  assigned to the Page component, NOT the CouponsPage.
				*
				* Solution here is to not assign the ref to this component IF the ref was contributed
				* by trancluded children and instead allow the parent to assign the ref.
				*/
				(<any> this.refs)[el.getAttribute("ref")] = el;
				el.removeAttribute("ref");
			}
		});
	}

	private _doNonVirtualTransclusion(dom: virtualDom.VNode) {
		this._transclude();

		// compute the transclusion
		const DEFAULT_SLOT = "__[default]__";
		const slots: {[name: string]: boolean} = {};
		const walk = (el: virtualDom.VTree): virtualDom.VTree => {
			if (el instanceof virtualDom.VNode) {
				if (el.tagName === "content") {
					const attributes = el.properties.attributes || {};
					const slot = attributes["slot"];
					const isDefault = !("slot" in attributes);

					if (slot === DEFAULT_SLOT)
						throw new Error(`Slot name '${DEFAULT_SLOT}' is reserved.`);
					if (isDefault && slots[DEFAULT_SLOT])
						throw new Error(`More than one unnamed content slot not allowed.`);
					if (!isDefault && slots[slot])
						throw new Error(`Slot '${slot}' already registered.`);
					slots[isDefault ? DEFAULT_SLOT : slot] = true;

					// place the transcluded elements
					// FIXME handle error case where transcluded node's `content-slot` is
					// not found.
					const transcludedNodes = this._transcludedContent.filter((el) =>
						el instanceof Element && (
							(!el.hasAttribute("content-slot") && isDefault) ||
							(el.getAttribute("content-slot") === slot)
					));

					return new ShadowDOMContentElementWidget(transcludedNodes);

					// if (!(slotName in slots)) throw new Error(
					//     `Slot '${slotName}' not found in '${this.tagName}'.`);
				} else {
					// This is neccessary since we are potentially 'rebuilding' the VTree
					// things like 'hasWidgets' would be broken changing inplace.
					return new virtualDom.VNode(el.tagName, el.properties,
						el.children.map(walk), el.key, el.namespace);
				}
			} else {
				return el;
			}
		};

		// this converts <content> elements into widgets
		const transformedSubTree = (<virtualDom.VNode> walk(dom));
		return new virtualDom.VNode(this.tagName, this.props, transformedSubTree.children);
	}

	private _doVirtualTransclusion(dom: virtualDom.VNode) {
		if (dom.tagName !== "template") return null;

		// compute the transclusion
		const DEFAULT_SLOT = "__[default]__";
		const slots: {[name: string]: boolean} = {};
		const walk = (el: virtualDom.VTree): virtualDom.VChild => {
			if (el instanceof virtualDom.VNode) {
				if (el.tagName === "content") {
					const attributes = el.properties.attributes || {};
					const slot = attributes["slot"];
					const isDefault = !("slot" in attributes);

					if (slot === DEFAULT_SLOT)
						throw new Error(`Slot name '${DEFAULT_SLOT}' is reserved.`);
					if (isDefault && slots[DEFAULT_SLOT])
						throw new Error(`More than one unnamed content slot not allowed.`);
					if (!isDefault && slots[slot])
						throw new Error(`Slot '${slot}' already registered.`);
					slots[isDefault ? DEFAULT_SLOT : slot] = true;

					// TODO: handle error case where transcluded node's `content-slot` is
					// not found.
					// if (!(slotName in slots)) throw new Error(
					//     `Slot '${slotName}' not found in '${this.tagName}'.`);

					// place the transcluded elements
					return this._transcludedVTree.filter((vtree) => {
						if (vtree instanceof virtualDom.VNode) {
							const attributes = vtree.properties.attributes || {};
							return (!("content-slot" in attributes) && isDefault) ||
											(attributes["content-slot"] === slot);
						} else {
							return isDefault;
						}
					});
				} else {
					// This is neccessary since we are potentially 'rebuilding' the VTree
					// things like 'hasWidgets' would be broken changing inplace.
					return new virtualDom.VNode(el.tagName, el.properties,
							array.flatten((<virtualDom.VTree[][]> el.children.map(walk))),
							el.key, el.namespace);
				}
			} else {
				return el;
			}
		};

		// this converts <content> elements into widgets
		const transformedSubTree = (<virtualDom.VNode> walk(dom));
		return new virtualDom.VNode(this.tagName, this.props, transformedSubTree.children);
	}

}
